import {ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {MAT_DIALOG_DATA} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {finalize, shareReplay, takeUntil} from 'rxjs/operators';
import {API} from '../../../shared/constants/api.constants';
import {UserSettings} from '../../../shared/models/user-settings.model';
import {MessageService} from '../../../shared/services/message-service.service';
import {UtilsService} from '../../../shared/services/utils.service';
import {ErrorHandlingHttpClient} from '../../../shared/services/error-handling-http-client.service';
import {LoaderService} from '../../../shared/services/loader.service';
import {SETTINGS_GROUPS, SettingsModalComponentData} from './settings-modal.model';


@Component({
  selector: 'app-settings-modal',
  templateUrl: './settings-modal.component.html',
  styleUrls: ['./settings-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SettingsModalComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>();
  private loadSettingsIntent = new BehaviorSubject(null);
  selectedIndex = [SETTINGS_GROUPS.EMAIL_SIGNATURE];
  settings$: Observable<UserSettings>;
  settingsLoading$ = new BehaviorSubject(false);
  form!: FormGroup;
  testId = 'settings_modal';

  constructor(
    @Inject(MAT_DIALOG_DATA) dialogData: SettingsModalComponentData,
    private errorHandlingHttpClient: ErrorHandlingHttpClient,
    private loaderService: LoaderService,
    private fb: FormBuilder,
    private messageService: MessageService,
    private translate: TranslateService
  ) {
    this.settings$ = this.errorHandlingHttpClient.get(API.userSettings());
    this.selectedIndex = [dialogData.section];
  }

  ngOnInit(): void {
    this.initForm();
    this.loadSettingsIntent.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.loaderService.showLoader();
      this.settings$
        .pipe(
          finalize(() => {
            this.loaderService.hideLoader();
          }),
          takeUntil(this.ngUnsubscribe),
          shareReplay({
            refCount: false,
            bufferSize: 1
          })
        )
        .subscribe(settings => {
          this.form.patchValue(settings);
        });
    });
  }
  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private initForm() {
    this.form = this.fb.group({
      displayMode: this.fb.control(null),
      emailSignature: this.fb.control(null, [UtilsService.maxBytesLength(1 * 1000 * 1000, '1 MB')])
    });
  }

  async submitForm(selectedIndex) {
    switch (selectedIndex) {
      case SETTINGS_GROUPS.EMAIL_SIGNATURE: {
        try {
          const field = this.form.get('emailSignature');
          field.markAllAsTouched();
          if (field.valid) {
            this.loaderService.showLoader();
            await this.errorHandlingHttpClient
              .put(API.userEmailSignature(), {
                value: field.value || ''
              })
              .toPromise();

            this.messageService.showSystemMessage('success', 'Settings were successfully saved', '');
          } else {
            this.messageService.showSystemMessage(
              'warning',
              this.translate.instant('common.modal.errors.invalid-form-header'),
              this.translate.instant('common.modal.errors.invalid-form-desc'),
              5000
            );
          }
        } finally {
          this.loaderService.hideLoader();
        }
      }
    }
  }
}
